/**
 * History — Undo/redo command stack
 * Per PLAN.md core/History.js
 */
import { eventBus, Events } from './EventBus.js';

/**
 * Command interface for undo/redo
 * Each command must implement execute() and undo()
 */
export class Command {
    execute() {
        throw new Error('Command.execute() must be implemented');
    }

    undo() {
        throw new Error('Command.undo() must be implemented');
    }
}

/**
 * History manager using command pattern
 */
export class History {
    constructor(maxSize = 100) {
        this.undoStack = [];
        this.redoStack = [];
        this.maxSize = maxSize;
    }

    /**
     * Execute a command and add to history
     * @param {Command} command 
     */
    execute(command) {
        command.execute();
        this.undoStack.push(command);

        // Clear redo stack on new action
        this.redoStack = [];

        // Limit stack size
        if (this.undoStack.length > this.maxSize) {
            this.undoStack.shift();
        }

        eventBus.emit(Events.HISTORY_PUSH, {
            canUndo: this.canUndo(),
            canRedo: this.canRedo()
        });
    }

    /**
     * Undo the last command
     */
    undo() {
        if (!this.canUndo()) return;

        const command = this.undoStack.pop();
        command.undo();
        this.redoStack.push(command);

        eventBus.emit(Events.HISTORY_UNDO, {
            canUndo: this.canUndo(),
            canRedo: this.canRedo()
        });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Redo the last undone command
     */
    redo() {
        if (!this.canRedo()) return;

        const command = this.redoStack.pop();
        command.execute();
        this.undoStack.push(command);

        eventBus.emit(Events.HISTORY_REDO, {
            canUndo: this.canUndo(),
            canRedo: this.canRedo()
        });
        eventBus.emit(Events.RENDER_REQUEST);
    }

    /**
     * Check if undo is available
     * @returns {boolean}
     */
    canUndo() {
        return this.undoStack.length > 0;
    }

    /**
     * Check if redo is available
     * @returns {boolean}
     */
    canRedo() {
        return this.redoStack.length > 0;
    }

    /**
     * Clear all history
     */
    clear() {
        this.undoStack = [];
        this.redoStack = [];
    }
}

// Concrete command implementations

/**
 * Command to create a node
 */
export class CreateNodeCommand extends Command {
    constructor(document, node) {
        super();
        this.document = document;
        this.node = node;
    }

    execute() {
        this.document.addNode(this.node);
    }

    undo() {
        this.document.removeNode(this.node.id);
    }
}

/**
 * Command to delete a node
 */
export class DeleteNodeCommand extends Command {
    constructor(document, nodeId) {
        super();
        this.document = document;
        this.nodeId = nodeId;
        this.node = null;
        this.connections = [];
    }

    execute() {
        this.node = this.document.getNode(this.nodeId);
        // Store connected connections for undo
        this.connections = this.document.getConnectionsForNode(this.nodeId);
        this.document.removeNode(this.nodeId);
    }

    undo() {
        if (this.node) {
            this.document.addNode(this.node);
            // Restore connections
            for (const conn of this.connections) {
                this.document.addConnection(conn);
            }
        }
    }
}

/**
 * Command to move node(s)
 */
export class MoveNodesCommand extends Command {
    constructor(document, nodeIds, deltaX, deltaY) {
        super();
        this.document = document;
        this.nodeIds = nodeIds;
        this.deltaX = deltaX;
        this.deltaY = deltaY;
    }

    execute() {
        for (const id of this.nodeIds) {
            const node = this.document.getNode(id);
            if (node) {
                node.position.x += this.deltaX;
                node.position.y += this.deltaY;
            }
        }
    }

    undo() {
        for (const id of this.nodeIds) {
            const node = this.document.getNode(id);
            if (node) {
                node.position.x -= this.deltaX;
                node.position.y -= this.deltaY;
            }
        }
    }
}

/**
 * Command to update node content
 */
export class UpdateNodeCommand extends Command {
    constructor(document, nodeId, newContent, oldContent) {
        super();
        this.document = document;
        this.nodeId = nodeId;
        this.newContent = newContent;
        this.oldContent = oldContent;
    }

    execute() {
        const node = this.document.getNode(this.nodeId);
        if (node) {
            node.content = this.newContent;
        }
    }

    undo() {
        const node = this.document.getNode(this.nodeId);
        if (node) {
            node.content = this.oldContent;
        }
    }
}

/**
 * Command to create a connection
 */
export class CreateConnectionCommand extends Command {
    constructor(document, connection) {
        super();
        this.document = document;
        this.connection = connection;
    }

    execute() {
        this.document.addConnection(this.connection);
    }

    undo() {
        this.document.removeConnection(this.connection.id);
    }
}

/**
 * Command to delete a connection
 */
export class DeleteConnectionCommand extends Command {
    constructor(document, connectionId) {
        super();
        this.document = document;
        this.connectionId = connectionId;
        this.connection = null;
    }

    execute() {
        this.connection = this.document.getConnection(this.connectionId);
        this.document.removeConnection(this.connectionId);
    }

    undo() {
        if (this.connection) {
            this.document.addConnection(this.connection);
        }
    }
}

/**
 * Command to group nodes
 */
export class GroupNodesCommand extends Command {
    constructor(document, group) {
        super();
        this.document = document;
        this.group = group;
        this.previousGroupIds = new Map();
    }

    execute() {
        // Store previous group assignments
        for (const nodeId of this.group.nodeIds) {
            const node = this.document.getNode(nodeId);
            if (node) {
                this.previousGroupIds.set(nodeId, node.groupId);
                node.groupId = this.group.id;
            }
        }
        this.document.addGroup(this.group);
    }

    undo() {
        this.document.removeGroup(this.group.id);
        // Restore previous assignments
        for (const [nodeId, prevGroupId] of this.previousGroupIds) {
            const node = this.document.getNode(nodeId);
            if (node) {
                node.groupId = prevGroupId;
            }
        }
    }
}

/**
 * Command to ungroup nodes
 */
export class UngroupNodesCommand extends Command {
    constructor(document, groupId) {
        super();
        this.document = document;
        this.groupId = groupId;
        this.group = null;
    }

    execute() {
        this.group = this.document.getGroup(this.groupId);
        if (this.group) {
            for (const nodeId of this.group.nodeIds) {
                const node = this.document.getNode(nodeId);
                if (node) {
                    node.groupId = null;
                }
            }
            this.document.removeGroup(this.groupId);
        }
    }

    undo() {
        if (this.group) {
            this.document.addGroup(this.group);
            for (const nodeId of this.group.nodeIds) {
                const node = this.document.getNode(nodeId);
                if (node) {
                    node.groupId = this.group.id;
                }
            }
        }
    }
}
